import { formatInLao } from "@/shared/lib/date-time";
import { escapeHtmlForPrintUnsafeContext } from "@/shared/lib/form-responses-print-a4";
import type { BrandPrintLanguage } from "@/shared/lib/brand-document-print-language";
import { getBrandPrintShellI18n } from "@/shared/lib/performance-brand-print-i18n";
import { format as formatDateFns } from "date-fns";
import { PRINT_DOCUMENT_FONT_FAMILY } from "@/shared/lib/print-fonts";

export const BRAND_PRINT = {
  name: "Fanglao Studio Sole Co, Ltd",
  phone: "020 52263111",
  addressLines: {
    lo: [
      "ວຽງຈັນເຊັນເຕີ ຊັ້ນ 4",
      "ບ້ານ ໜອງຈັນ, ເມືອງ ສີສັດຕະນາກ, ນະຄອນຫຼວງວຽງຈັນ",
    ],
    en: [
      "Vientiane Center, 4th Floor",
      "Nong Chan Village, Sisattanak District, Vientiane Capital",
    ],
  },
} as const;

export function getBrandPrintAddressLines(
  lang: BrandPrintLanguage,
): readonly string[] {
  return BRAND_PRINT.addressLines[lang];
}

export const BRAND_DOCUMENT_PRINT_STYLES_A4 = `
  @media print {
    @page {
      size: A4 portrait;
      margin: 10mm;
    }
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .bd-actions {
      display: none !important;
    }
    .bd-page {
      box-shadow: none !important;
      margin: 0 !important;
      min-height: auto !important;
      width: auto !important;
      padding: 0 !important;
      background: #fff !important;
    }
    .bd-print-footer {
      position: fixed !important;
      right: 0 !important;
      bottom: 0 !important;
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    .bd-doc-no-box {
      background: #fff !important;
    }
  }

  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 16px;
    font-family: ${PRINT_DOCUMENT_FONT_FAMILY};
    font-size: 15px;
    line-height: 1.5;
    color: #0a0a0a;
    background: #f4f4f5;
  }

  .bd-actions {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 12px;
  }
  .bd-actions button {
    border: 1px solid #d4d4d8;
    border-radius: 6px;
    background: #fff;
    padding: 8px 14px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
  }
  .bd-actions button.primary {
    border-color: #dc2626;
    background: #dc2626;
    color: #fff;
  }
  .bd-lang-switch {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 4px;
    font-size: 12px;
  }
  .bd-lang-switch a {
    padding: 6px 10px;
    border: 1px solid #d4d4d8;
    border-radius: 6px;
    text-decoration: none;
    color: #404040;
    font-weight: 600;
  }
  .bd-lang-switch a.active {
    border-color: #dc2626;
    color: #dc2626;
    background: #fef2f2;
  }

  .bd-page {
    display: flex;
    flex-direction: column;
    width: 210mm;
    min-height: 277mm;
    margin: 0 auto;
    padding: 10mm 12mm;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .bd-page.bd-page--landscape {
    width: 297mm;
    min-height: 194mm;
  }

  .bd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
  }
  .bd-brand-block {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 0;
  }
  .bd-logo {
    display: block;
    height: 80px;
    width: auto;
    max-width: 280px;
    object-fit: contain;
    object-position: left center;
  }
  .bd-logo--performance {
    height: 64px;
    max-width: 300px;
  }
  .bd-doc-meta {
    text-align: right;
    font-size: 13px;
    color: #404040;
    line-height: 1.45;
  }
  .bd-doc-meta p {
    margin: 0 0 2px;
  }
  .bd-doc-meta p:last-child {
    margin-bottom: 0;
  }

  .bd-print-footer {
    margin-top: auto;
    align-self: flex-end;
    padding-top: 12px;
    font-size: 12px;
    color: #737373;
    white-space: nowrap;
  }

  .bd-title {
    margin: 10px 0 2px;
    font-size: 34px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .bd-subtitle {
    margin: 0 0 10px;
    font-size: 13px;
    color: #525252;
  }

  .bd-doc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 10px;
  }
  .bd-doc-head-title {
    flex: 1;
    min-width: 0;
  }
  .bd-doc-head-title .bd-title {
    margin-top: 0;
    margin-bottom: 0;
  }
  .bd-doc-head-title .bd-subtitle {
    margin-bottom: 0;
  }

  .bd-doc-no-box {
    flex-shrink: 0;
    margin-bottom: 0;
    min-width: 48mm;
    max-width: 62mm;
    padding: 10px 12px;
    border: 1px solid #e5e5e5;
    border-left: 4px solid #dc2626;
    background: #fafafa;
    text-align: right;
  }
  .bd-doc-no-label {
    font-size: 13px;
    font-weight: 600;
    color: #525252;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 0 0 4px;
  }
  .bd-doc-no-value {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1.15;
    color: #0a0a0a;
    word-break: break-word;
  }
  .bd-doc-no-date {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.2;
    color: #525252;
    letter-spacing: 0.02em;
  }

  .bd-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px 16px;
    margin-bottom: 10px;
  }
  .bd-detail-sections {
    display: grid;
    gap: 8px 16px;
    margin-bottom: 10px;
  }
  .bd-detail-sections--cols-1 {
    grid-template-columns: 1fr;
  }
  .bd-detail-sections--cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .bd-detail-sections--cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .bd-detail-section {
    min-width: 0;
  }
  .bd-detail-section-title {
    margin: 0 0 4px;
    padding-bottom: 3px;
    border-bottom: 1px solid #e5e5e5;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #525252;
    line-height: 1.25;
  }
  .bd-detail-section .bd-grid {
    margin-bottom: 0;
    /* Shared label/value columns so every value starts on one vertical line */
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 3px;
    align-items: baseline;
  }
  .bd-detail-section--cols-2 .bd-grid {
    grid-template-columns: max-content minmax(0, 1fr) max-content minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 3px;
  }
  .bd-detail-section--full {
    grid-column: 1 / -1;
  }
  .bd-detail-field {
    /* Let label + value participate in the section grid for column alignment */
    display: contents;
  }
  .bd-detail-section .bd-field-label {
    font-size: 10px;
    margin: 0;
    letter-spacing: 0.03em;
    line-height: 1.35;
  }
  .bd-detail-section .bd-field-value {
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    line-height: 1.35;
  }
  @media print {
    .bd-detail-sections {
      gap: 7px 14px;
      margin-bottom: 8px;
    }
  }
  .bd-field-label {
    font-size: 12px;
    font-weight: 600;
    color: #737373;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 2px;
  }
  .bd-field-value {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #171717;
    word-break: break-word;
  }

  .bd-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 12px;
  }
  .bd-table th {
    background: #fafafa;
    color: #525252;
    font-weight: 700;
    text-align: left;
    border: 1px solid #e5e5e5;
    padding: 8px;
  }
  .bd-table td {
    border: 1px solid #e5e5e5;
    padding: 8px;
    vertical-align: top;
    color: #171717;
  }
  .bd-table .num {
    text-align: right;
    white-space: nowrap;
  }
  .bd-table-empty {
    text-align: center;
    color: #737373;
    padding: 14px !important;
  }

  .bd-summary-bank-layout {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10mm;
    margin-top: 14px;
  }
  .bd-summary-bank-layout .bd-bank-account {
    flex: 1;
    margin-top: 0;
    min-width: 0;
  }
  .bd-summary-bank-layout .bd-summary {
    margin-left: 0;
    margin-top: 0;
    width: 78mm;
    flex: 0 0 78mm;
  }

  .bd-summary {
    margin-left: auto;
    margin-top: 14px;
    width: 78mm;
  }
  .bd-summary-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #e5e5e5;
    padding: 6px 0;
    font-size: 13px;
  }
  .bd-summary-row.grand {
    font-size: 16px;
    font-weight: 800;
    border-bottom: 2px solid #dc2626;
    padding-top: 4px;
    margin-top: 2px;
  }

  .bd-notes {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e5e5e5;
    font-size: 12px;
    line-height: 1.55;
    color: #404040;
  }
  .bd-notes-title {
    margin: 0 0 4px;
    font-size: 13px;
    font-weight: 700;
    color: #171717;
  }

  .bd-bank-account {
    margin-top: 12px;
    font-size: 12px;
    line-height: 1.55;
    color: #404040;
  }
  .bd-bank-account-body {
    font-size: 12px;
    line-height: 1.55;
    color: #404040;
  }
  .bd-bank-account-body p {
    margin: 0 0 4px;
  }
  .bd-bank-account-body p:last-child {
    margin-bottom: 0;
  }
  .bd-bank-account-body ul,
  .bd-bank-account-body ol {
    margin: 0;
    padding-left: 18px;
  }
  .bd-bank-account-body li {
    margin-bottom: 4px;
  }
  .bd-bank-account-body li:last-child {
    margin-bottom: 0;
  }
  .bd-bank-account-body strong {
    font-weight: 700;
  }
  .bd-bank-account-body em {
    font-style: italic;
  }
  .bd-bank-account-body a {
    color: inherit;
    text-decoration: underline;
  }
  .bd-bank-account-body h1,
  .bd-bank-account-body h2,
  .bd-bank-account-body h3,
  .bd-bank-account-body h4,
  .bd-bank-account-body h5,
  .bd-bank-account-body h6 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    color: #171717;
  }

  .bd-payment-terms {
    margin-top: 12px;
  }
  .bd-payment-terms-list {
    margin: 0;
    padding-left: 18px;
    font-size: 12px;
    line-height: 1.55;
    color: #404040;
  }
  .bd-payment-terms-list li {
    margin-bottom: 4px;
  }
  .bd-payment-terms-list li:last-child {
    margin-bottom: 0;
  }
  .bd-payment-terms-body {
    font-size: 12px;
    line-height: 1.55;
    color: #404040;
  }
  .bd-payment-terms-body p {
    margin: 0 0 4px;
  }
  .bd-payment-terms-body p:last-child {
    margin-bottom: 0;
  }
  .bd-payment-terms-body ul,
  .bd-payment-terms-body ol {
    margin: 0;
    padding-left: 18px;
  }
  .bd-payment-terms-body li {
    margin-bottom: 4px;
  }
  .bd-payment-terms-body li:last-child {
    margin-bottom: 0;
  }
  .bd-payment-terms-body strong {
    font-weight: 700;
  }
  .bd-payment-terms-body em {
    font-style: italic;
  }
  .bd-payment-terms-body a {
    color: inherit;
    text-decoration: underline;
  }
  .bd-payment-terms-body h1,
  .bd-payment-terms-body h2,
  .bd-payment-terms-body h3,
  .bd-payment-terms-body h4,
  .bd-payment-terms-body h5,
  .bd-payment-terms-body h6 {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    color: #171717;
  }

  .bd-installment-plan {
    margin-top: 12px;
  }
  .bd-installment-table {
    margin-top: 6px;
    font-size: 11px;
  }

  .bd-receipt-payment {
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e5e5e5;
  }
  .bd-receipt-payment-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px 20px;
    margin-bottom: 10px;
    max-width: 120mm;
  }
  .bd-receipt-payment-meta-item {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 12px;
    color: #404040;
  }
  .bd-receipt-payment-meta-item strong {
    color: #171717;
    font-weight: 600;
    text-align: right;
  }
  .bd-receipt-payment-summary {
    margin-top: 0;
    width: 88mm;
  }
  .bd-summary-row--receipt-current {
    font-size: 14px;
    font-weight: 700;
    color: #171717;
    background: #fafafa;
    margin: 0 -4px;
    padding: 6px 4px;
    border-bottom-color: #d4d4d4;
  }
  .bd-summary-row--receipt-current strong {
    color: #dc2626;
  }

  .bd-signatures {
    margin-top: 14px;
    padding-top: 0;
  }
  .bd-signatures-title {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 700;
    color: #171717;
  }
  .bd-signatures-intro {
    margin: 0 0 12px;
    font-size: 12px;
    line-height: 1.4;
    color: #404040;
  }
  .bd-signatures-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .bd-signatures-grid--studio-only {
    grid-template-columns: 1fr;
    justify-items: end;
  }
  .bd-signatures-grid--studio-only .bd-signature-block--right {
    width: 48%;
    min-width: 72mm;
    max-width: 88mm;
  }
  .bd-signatures-grid--columns-3 {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .bd-signature-block--column {
    text-align: center;
  }
  .bd-signature-block--column .bd-signature-date-line {
    justify-content: center;
  }
  .bd-signature-block {
    break-inside: avoid;
    page-break-inside: avoid;
    padding: 12px 14px;
    min-height: 120px;
  }
  .bd-signature-block--left {
    text-align: left;
  }
  .bd-signature-block--right {
    text-align: right;
  }
  .bd-signature-block--right .bd-signature-date-line {
    justify-content: flex-end;
  }
  .bd-signature-block--right .bd-studio-signature-img {
    margin-left: auto;
    object-position: right center;
  }
  .bd-signature-party {
    margin: 0 0 12px;
    font-size: 13px;
    font-weight: 700;
    color: #171717;
    text-decoration: underline;
  }
  .bd-signature-row {
    margin-bottom: 14px;
  }
  .bd-signature-row:last-child {
    margin-bottom: 0;
  }
  .bd-signature-row--date {
    margin-bottom: 0;
  }
  .bd-signature-date-line {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #171717;
  }
  .bd-signature-label {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #737373;
    letter-spacing: 0.03em;
  }
  .bd-signature-value {
    margin: 0;
    min-height: 28px;
    font-size: 14px;
    font-weight: 600;
    color: #171717;
    word-break: break-word;
  }
  .bd-signature-value--sign {
    min-height: 52px;
  }
  .bd-signature-row--studio-sign {
    margin-bottom: 0;
  }
  .bd-studio-signature-img {
    display: block;
    max-height: 192px;
    max-width: 100%;
    width: auto;
    object-fit: contain;
  }

  /* Auto-compact layout when quotation has many line items (>8). */
  .bd-page.bd-page--compact {
    padding: 6mm 8mm;
    min-height: auto;
  }
  .bd-page.bd-page--compact .bd-logo {
    height: 48px;
  }
  .bd-page.bd-page--compact .bd-logo--performance {
    height: 42px;
  }
  .bd-page.bd-page--compact .bd-header {
    margin-bottom: 2px;
  }
  .bd-page.bd-page--compact .bd-doc-meta {
    font-size: 10px;
    line-height: 1.3;
  }
  .bd-page.bd-page--compact .bd-title {
    font-size: 22px;
  }
  .bd-page.bd-page--compact .bd-doc-head {
    margin-bottom: 6px;
  }
  .bd-page.bd-page--compact .bd-detail-sections {
    gap: 5px 12px;
    margin-bottom: 6px;
  }
  .bd-page.bd-page--compact .bd-detail-section-title {
    margin-bottom: 2px;
    padding-bottom: 2px;
  }
  .bd-page.bd-page--compact .bd-detail-section .bd-field-value {
    font-size: 11px;
  }
  .bd-page.bd-page--compact .bd-table {
    margin-top: 4px;
    font-size: 10px;
  }
  .bd-page.bd-page--compact .bd-table th,
  .bd-page.bd-page--compact .bd-table td {
    padding: 3px 5px;
  }
  .bd-page.bd-page--compact .bd-summary,
  .bd-page.bd-page--compact .bd-summary-bank-layout {
    margin-top: 8px;
  }
  .bd-page.bd-page--compact .bd-summary-row {
    font-size: 11px;
  }
  .bd-page.bd-page--compact .bd-summary-row--total {
    font-size: 13px;
  }
  .bd-page.bd-page--compact .bd-notes,
  .bd-page.bd-page--compact .bd-payment-terms,
  .bd-page.bd-page--compact .bd-bank-account {
    margin-top: 8px;
    font-size: 10px;
    line-height: 1.35;
  }
  .bd-page.bd-page--compact .bd-notes-title {
    font-size: 11px;
    margin-bottom: 2px;
  }
  .bd-page.bd-page--compact .bd-print-footer {
    padding-top: 6px;
    font-size: 10px;
  }
  @media print {
    .bd-page.bd-page--compact .bd-detail-sections {
      margin-bottom: 5px;
    }
  }
`;

const SIGNATURE_DATE_PLACEHOLDER = "..../..../....";

export type BrandPrintMetaField = {
  label: string;
  value: string;
};

export type BrandPrintDetailSection = {
  title: string;
  fields: BrandPrintMetaField[];
  /** Field columns inside the section (default 1) */
  columns?: 1 | 2;
  /** Span full width of the detail grid */
  fullWidth?: boolean;
};

export type BrandPrintPageSize = "A4 portrait" | "A4 landscape";

export type BrandPrintContentDensity = "normal" | "compact";

/** Revenue lines above this count use compact print layout (quotation/invoice). */
export const BRAND_PRINT_COMPACT_LINE_THRESHOLD = 8;

export type BrandPrintDocumentParams = {
  docTitle: string;
  titleLo: string;
  titleEn: string;
  documentNo: string;
  documentNoLabel?: string;
  /** Issue / document date shown under the document number. */
  documentIssuedAt?: Date | string | null;
  printedAt?: Date;
  /** Print language (default Lao). */
  language?: BrandPrintLanguage;
  /** Links to reload the same document in another language. */
  languageSwitch?: { lo: string; en: string };
  /** Page orientation for @page rule (default portrait). */
  pageSize?: BrandPrintPageSize;
  /** Tighter layout for long documents (auto when line count > threshold). */
  contentDensity?: BrandPrintContentDensity;
  metaFields: BrandPrintMetaField[];
  /** Grouped meta blocks (customer / event / document). Falls back to metaFields grid when omitted. */
  detailSections?: BrandPrintDetailSection[];
  contentHtml: string;
  signatures?: {
    /** Section title; omit or null to hide */
    title?: string | null;
    /** Intro paragraph; omit or null to hide */
    intro?: string | null;
    /** Omit or null to hide the left signature block */
    leftParty?: string | null;
    rightParty?: string;
    /** When false, studio block shows blank lines instead of stamp/signature image */
    showStudioSignature?: boolean;
    /** Equal-width blank signature columns (no images). Overrides left/right layout. */
    partyColumns?: string[];
  };
};

const BRAND_PRINT_PAGE_MARGINS: Record<BrandPrintPageSize, string> = {
  "A4 portrait": "10mm",
  "A4 landscape": "8mm",
};

export function getBrandDocumentPrintStylesA4(
  pageSize: BrandPrintPageSize = "A4 portrait",
): string {
  return BRAND_DOCUMENT_PRINT_STYLES_A4.replace(
    "size: A4 portrait;",
    `size: ${pageSize};`,
  ).replace("margin: 10mm;", `margin: ${BRAND_PRINT_PAGE_MARGINS[pageSize]};`);
}

export type BrandPrintAssets = {
  logoDataUrl?: string;
  signatureDataUrl?: string;
  /** Use performance logo sizing (perform-logo.png). Default = studio mark. */
  logoVariant?: "default" | "performance";
  /** Embedded @font-face CSS (Noto Sans Lao) for standalone print HTML. */
  fontFaceCss?: string;
};

import type { DocumentCurrency } from "@/shared/lib/performance-document-currency";
import { formatDocumentMoney } from "@/shared/lib/performance-document-currency";

export function formatPrintMoney(
  value: number,
  lang: BrandPrintLanguage = "lo",
  currency: DocumentCurrency = "LAK",
): string {
  return formatDocumentMoney(value, currency, { language: lang });
}

export function formatPrintDate(
  value: Date | string | null | undefined,
  lang: BrandPrintLanguage = "lo",
): string {
  if (!value) return "—";
  if (lang === "en") {
    const dt = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return formatDateFns(dt, "dd/MM/yyyy");
  }
  const formatted = formatInLao(value, "dd/MM/yyyy");
  return formatted === "–" ? "—" : formatted;
}

export function formatPrintDateTime(
  value: Date | string | null | undefined,
  lang: BrandPrintLanguage = "lo",
): string {
  if (!value) return "—";
  if (lang === "en") {
    const dt = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dt.getTime())) return "—";
    return formatDateFns(dt, "dd/MM/yyyy HH:mm");
  }
  const formatted = formatInLao(value, "dd/MM/yyyy HH:mm");
  return formatted === "–" ? "—" : formatted;
}

export function escapePrintText(value: string | null | undefined): string {
  return escapeHtmlForPrintUnsafeContext(value ?? "");
}

function renderDetailField(label: string, value: string): string {
  return `
    <div class="bd-detail-field">
      <p class="bd-field-label">${escapePrintText(label)}</p>
      <p class="bd-field-value">${escapePrintText(value || "—")}</p>
    </div>
  `;
}

function renderDetailSections(sections: BrandPrintDetailSection[]): string {
  const visible = sections.filter((section) => section.fields.length > 0);
  if (visible.length === 0) return "";

  const colCount = Math.min(Math.max(visible.length, 1), 3);
  const blocks = visible
    .map((section) => {
      const sectionClass = [
        "bd-detail-section",
        section.columns === 2 ? "bd-detail-section--cols-2" : "",
        section.fullWidth ? "bd-detail-section--full" : "",
      ]
        .filter(Boolean)
        .join(" ");

      return `
    <section class="${sectionClass}">
      <h2 class="bd-detail-section-title">${escapePrintText(section.title)}</h2>
      <div class="bd-grid">
        ${section.fields.map((field) => renderDetailField(field.label, field.value)).join("")}
      </div>
    </section>`;
    })
    .join("");

  return `<div class="bd-detail-sections bd-detail-sections--cols-${colCount}">${blocks}</div>`;
}

function renderMetaField(label: string, value: string): string {
  return `
    <div>
      <p class="bd-field-label">${escapePrintText(label)}</p>
      <p class="bd-field-value">${escapePrintText(value || "—")}</p>
    </div>
  `;
}

function renderSignatureDateRow(label: string): string {
  return `
    <div class="bd-signature-row bd-signature-row--date">
      <p class="bd-signature-date-line">
        <span class="bd-signature-label">${escapePrintText(label)}</span>
        <span>${escapePrintText(SIGNATURE_DATE_PLACEHOLDER)}</span>
      </p>
    </div>
  `;
}

function renderBlankSignatureRow(): string {
  return `
    <div class="bd-signature-row">
      <p class="bd-signature-value bd-signature-value--sign">&nbsp;</p>
    </div>
  `;
}

function renderSignaturesSection(
  params: NonNullable<BrandPrintDocumentParams["signatures"]>,
  studioSignatureDataUrl: string | undefined,
  shell: ReturnType<typeof getBrandPrintShellI18n>,
): string {
  const title =
    params.title !== undefined ? params.title : shell.signatureTitleDefault;
  const intro =
    params.intro !== undefined ? params.intro : shell.signatureIntroDefault;

  if (params.partyColumns && params.partyColumns.length > 0) {
    const blocks = params.partyColumns
      .map(
        (party) => `
        <div class="bd-signature-block bd-signature-block--column">
          <p class="bd-signature-party">${escapePrintText(party)}</p>
          ${renderBlankSignatureRow()}
          ${renderSignatureDateRow(shell.signatureDate)}
        </div>`,
      )
      .join("");

    return `
    <section class="bd-signatures">
      ${title ? `<h2 class="bd-signatures-title">${escapePrintText(title)}</h2>` : ""}
      <div class="bd-signatures-grid bd-signatures-grid--columns-3">
        ${blocks}
      </div>
    </section>
  `;
  }

  const studioSignBlock =
    params.showStudioSignature === false
      ? `${renderBlankSignatureRow()}${renderSignatureDateRow(shell.signatureDate)}`
      : studioSignatureDataUrl
        ? `
        <div class="bd-signature-row bd-signature-row--studio-sign">
          <img src="${studioSignatureDataUrl}" alt="" class="bd-studio-signature-img" />
        </div>
      `
        : `${renderBlankSignatureRow()}${renderSignatureDateRow(shell.signatureDate)}`;

  const rightParty = params.rightParty ?? "Fanglao Studio";

  const leftBlock =
    params.leftParty != null && params.leftParty !== ""
      ? `
        <div class="bd-signature-block bd-signature-block--left">
          <p class="bd-signature-party">${escapePrintText(params.leftParty)}</p>
          ${renderBlankSignatureRow()}
          ${renderSignatureDateRow(shell.signatureDate)}
        </div>`
      : "";

  const gridClass =
    leftBlock === ""
      ? "bd-signatures-grid bd-signatures-grid--studio-only"
      : "bd-signatures-grid";

  return `
    <section class="bd-signatures">
      ${title ? `<h2 class="bd-signatures-title">${escapePrintText(title)}</h2>` : ""}
      ${intro ? `<p class="bd-signatures-intro">${escapePrintText(intro)}</p>` : ""}
      <div class="${gridClass}">
        ${leftBlock}
        <div class="bd-signature-block bd-signature-block--right">
          <p class="bd-signature-party">${escapePrintText(rightParty)}</p>
          ${studioSignBlock}
        </div>
      </div>
    </section>
  `;
}

export async function buildBrandPrintDocumentHtml(
  params: BrandPrintDocumentParams,
  assets?: BrandPrintAssets,
): Promise<string> {
  // Asset loading uses Node APIs — pass via loadBrandPrintAssetsServer()
  // / loadPerformanceBrandPrintAssetsServer() from brand-document-print-a4.server.ts
  const brandAssets = assets ?? {};
  const lang = params.language ?? "lo";
  const shell = getBrandPrintShellI18n(lang);
  const pageStyles = getBrandDocumentPrintStylesA4(params.pageSize);
  const isPerformanceLogo = brandAssets.logoVariant === "performance";
  const brandBlockClass = "bd-brand-block";
  const logoClass = isPerformanceLogo
    ? "bd-logo bd-logo--performance"
    : "bd-logo";
  const logoBlock = brandAssets.logoDataUrl
    ? `<img src="${brandAssets.logoDataUrl}" alt="" class="${logoClass}" />`
    : `<p class="bd-title" style="margin:0;font-size:20px;">${escapePrintText(BRAND_PRINT.name)}</p>`;

  const printedAt = escapePrintText(
    lang === "en"
      ? formatDateFns(params.printedAt ?? new Date(), "dd/MM/yyyy")
      : formatInLao(params.printedAt ?? new Date(), "dd/MM/yyyy"),
  );
  const docNoLabel = params.documentNoLabel ?? shell.documentNoDefault;
  const docNoDate = params.documentIssuedAt
    ? formatPrintDate(params.documentIssuedAt, lang)
    : null;
  const displayTitle =
    lang === "en" ? params.titleEn : params.titleLo;

  const addressLinesHtml = getBrandPrintAddressLines(lang)
    .map((line) => `<p>${escapePrintText(line)}</p>`)
    .join("");

  const metaHtml = params.metaFields
    .map((field) => renderMetaField(field.label, field.value))
    .join("");

  const detailsHtml = params.detailSections?.length
    ? renderDetailSections(params.detailSections)
    : `<div class="bd-grid">${metaHtml}</div>`;

  const signaturesHtml = params.signatures
    ? renderSignaturesSection(
        params.signatures,
        brandAssets.signatureDataUrl,
        shell,
      )
    : "";

  const languageSwitchHtml = params.languageSwitch
    ? `<span class="bd-lang-switch">
        <a href="${escapePrintText(params.languageSwitch.lo)}" class="${lang === "lo" ? "active" : ""}">${escapePrintText(shell.languageLo)}</a>
        <a href="${escapePrintText(params.languageSwitch.en)}" class="${lang === "en" ? "active" : ""}">${escapePrintText(shell.languageEn)}</a>
      </span>`
    : "";

  const bodyHtml = `
    <div class="bd-actions">
      <button type="button" class="primary" onclick="window.print()">${escapePrintText(shell.printSavePdf)}</button>
      <button type="button" onclick="window.close()">${escapePrintText(shell.close)}</button>
      ${languageSwitchHtml}
    </div>
    <div class="bd-page${
      params.pageSize === "A4 landscape" ? " bd-page--landscape" : ""
    }${params.contentDensity === "compact" ? " bd-page--compact" : ""}">
      <header class="bd-header">
        <div class="${brandBlockClass}">${logoBlock}</div>
        <div class="bd-doc-meta">
          <p>${escapePrintText(BRAND_PRINT.name)}</p>
          ${addressLinesHtml}
          <p>${escapePrintText(shell.phonePrefix)} ${escapePrintText(BRAND_PRINT.phone)}</p>
        </div>
      </header>

      <div class="bd-doc-head">
        <div class="bd-doc-head-title">
          <h1 class="bd-title">${escapePrintText(displayTitle)}</h1>
        </div>
        <div class="bd-doc-no-box">
          <p class="bd-doc-no-label">${escapePrintText(docNoLabel)}</p>
          <p class="bd-doc-no-value">${escapePrintText(params.documentNo)}</p>
          ${
            docNoDate && docNoDate !== "—"
              ? `<p class="bd-doc-no-date">${escapePrintText(docNoDate)}</p>`
              : ""
          }
        </div>
      </div>

      ${detailsHtml}

      ${params.contentHtml}

      ${signaturesHtml}

      <footer class="bd-print-footer">${escapePrintText(shell.printedAtPrefix)}: ${printedAt}</footer>
    </div>
  `;

  return `<!DOCTYPE html>
<html lang="${lang === "en" ? "en" : "lo"}">
  <head>
    <meta charset="utf-8" />
    <title>${escapePrintText(params.docTitle)}</title>
    <style>${brandAssets.fontFaceCss ?? ""}${pageStyles}</style>
  </head>
  <body>${bodyHtml}</body>
</html>`;
}
